<template>
  <div class="dashboard-container">
    <el-container class="dashboard-layout"> 
      <el-main class="dashboard-main">
        <div class="content-with-sidebar">
          <div class="main-content">
            <div class="welcome-section">
              <h2>欢迎来到手工钩织社区</h2>
              <p>发现最新最精彩的手工钩针作品</p>
            </div>
            
            <!-- 轮播图部分 -->
            <div class="carousel-wrapper">
              <el-carousel 
                :height="carouselHeight" 
                indicator-position="outside"
                :autoplay="true"
                :interval="5000"
                arrow="hover"
              >
                <el-carousel-item 
                  v-for="post in posts" 
                  :key="post.id"
                  @click="goToPostDetail(post.id)"
                  class="carousel-item"
                >
                  <div class="carousel-card">
                    <div 
                      v-if="post.coverImage" 
                      class="carousel-image-wrapper"
                    >
                      <img 
                        :src="post.coverImage" 
                        alt="帖子封面"
                        class="carousel-image"
                      />
                    </div>
                    <div v-else class="default-image">
                      <i class="crochet-placeholder-icon">_hooks</i>
                      <p>暂无图片</p>
                    </div>
                    
                    <div class="carousel-content-overlay">
                      <div class="carousel-content">
                        <h3 class="carousel-title">{{ post.title }}</h3>
                        <p class="carousel-description">{{ post.content.substring(0, 50) }}{{ post.content.length > 50 ? '...' : '' }}</p>
                        
                        <div class="carousel-meta">
                          <div class="meta-item">
                            <el-icon><View /></el-icon>
                            <span>{{ post.viewCount }}</span>
                          </div>
                          <div class="meta-item">
                            <el-icon><Star /></el-icon>
                            <span>{{ post.likeCount }}</span>
                          </div>
                          <div class="meta-item">
                            <el-icon><Star /></el-icon>
                            <span>{{ post.favoriteCount }}</span>
                          </div>
                          <div class="meta-item">
                            <el-icon><ChatLineRound /></el-icon>
                            <span>{{ post.commentCount }}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </el-carousel-item>
              </el-carousel>
            </div>
            
          </div>
          
          <!-- 右侧公告悬浮窗 -->
          <div class="announcement-sidebar">
            <div class="announcement-card">
              <div class="announcement-header">
                <h3>📢 系统公告</h3>
                <span class="announcement-count">{{ topAnnouncements.length }} 条</span>
              </div>
              
              <div class="announcement-list">
                <div 
                  v-for="announcement in topAnnouncements" 
                  :key="announcement.id" 
                  class="announcement-item"
                  @click="goToAnnouncementCenter()"
                >
                  <h4 class="announcement-title">{{ announcement.title }}</h4>
                  <p class="announcement-preview">{{ announcement.content.substring(0, 60) }}{{ announcement.content.length > 60 ? '...' : '' }}</p>
                  <div class="announcement-time">{{ formatDate(announcement.publishTime) }}</div>
                </div>
                
                <div v-if="topAnnouncements.length === 0" class="no-announcements">
                  <p>暂无公告</p>
                </div>
              </div>
              
              <div class="announcement-footer">
                <el-button 
                  type="primary" 
                  plain 
                  @click="goToAnnouncementCenter()"
                  :disabled="topAnnouncements.length === 0"
                >
                  查看全部
                </el-button>
              </div>
            </div>
          </div>
        </div>
      </el-main>
    </el-container>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, onMounted, onUnmounted } from 'vue';
import { ElCarousel, ElCarouselItem, ElIcon, ElButton } from 'element-plus';
import { useRouter } from 'vue-router';
import { getUserDashboardData, PostItem } from '@/user/api/dashboard';
import { userAnnouncementApi, AnnouncementVO, TopAnnouncement } from '@/user/api/announcement';
import { View, Star, ChatLineRound } from '@element-plus/icons-vue';
import { formatDate } from '@/utils/date';

export default defineComponent({
  name: 'DashboardView',
  components: {
    ElCarousel,
    ElCarouselItem,
    ElIcon,
    ElButton,
    View,
    Star,
    ChatLineRound
  },
  setup() {
    const posts = ref<PostItem[]>([]);
    const topAnnouncements = ref<TopAnnouncement[]>([]);
    const carouselHeight = ref('400px'); // 默认轮播图高度
    const router = useRouter();

    const loadDashboardData = async () => {
      try {
        console.log('开始获取首页数据...');
        const response = await getUserDashboardData();
        console.log('首页数据响应:', response);
        if (response.data) {
          posts.value = response.data;
          console.log('帖子数据加载完成，共', posts.value.length, '条');
        }
      } catch (error) {
        console.error('获取首页数据失败:', error);
      }
    };

    // 加载前5条公告数据
    const loadTopAnnouncements = async () => {
      try {
        console.log('开始获取前5条公告数据...');
        console.log('调用API: userAnnouncementApi.getTopFiveAnnouncements()');
        const response = await userAnnouncementApi.getTopFiveAnnouncements();
        console.log('获取前5条公告响应:', response);
        
        // 修复：API返回的数据结构是 {code, msg, data}，实际公告数据在 response.data 中
        if (response && response.data && Array.isArray(response.data)) {
          console.log('获取到的公告数量:', response.data.length);
          console.log('公告数据:', response.data);
          topAnnouncements.value = response.data;
        } else {
          console.log('响应中没有有效的公告数据:', response);
          topAnnouncements.value = [];
        }
      } catch (error: any) {
        console.error('获取前5条公告数据失败:', error);
        console.error('错误详情:', error.response || error.message || error);
        topAnnouncements.value = [];
      }
    };

    const goToPostDetail = (postId: number) => {
      router.push(`/post/${postId}`);
    };

    const goToAnnouncementCenter = () => {
      router.push('/announcements');
    };

    onMounted(() => {
      console.log('DashboardView mounted');
      loadDashboardData();
      loadTopAnnouncements();
      // 根据屏幕尺寸调整轮播图高度
      updateCarouselHeight();
      window.addEventListener('resize', updateCarouselHeight);
    });

    const updateCarouselHeight = () => {
      const screenWidth = window.innerWidth;
      if (screenWidth < 768) {
        carouselHeight.value = '250px';
      } else {
        carouselHeight.value = '400px';
      }
    };

    // 组件销毁时移除事件监听器
    onUnmounted(() => {
      window.removeEventListener('resize', updateCarouselHeight);
    });

    return {
      posts,
      topAnnouncements,
      carouselHeight,
      goToPostDetail,
      goToAnnouncementCenter,
      formatDate
    };
  }
});
</script>

<style scoped>
.dashboard-container {
  min-height: 100vh;
  background: linear-gradient(135deg, #fefefe 0%, #f8f3eb 100%);
  font-family: 'Nunito', 'Quicksand', 'Open Sans', sans-serif;
  position: relative;
  overflow-x: hidden;
}

.dashboard-container::before {
  content: "";
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-image: 
    radial-gradient(#d4c19c 1px, transparent 1px),
    radial-gradient(#d4c19c 1px, transparent 1px);
  background-size: 40px 40px;
  background-position: 0 0, 20px 20px;
  opacity: 0.05;
  z-index: -1;
  pointer-events: none;
}

.dashboard-layout {
  max-width: 1400px;
  margin: 0 auto;
}

.dashboard-header {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #f9f2eb 0%, #fdfaf3 100%);
  border-bottom: 1px solid #e9dcc8;
  padding: 40px 0;
  border-radius: 0 0 20px 20px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  overflow: hidden;
}

.header-content {
  position: relative;
  z-index: 2;
}

.dashboard-header h1 {
  color: #8B7D6B;
  font-size: 36px;
  margin-bottom: 12px;
  text-align: center;
  font-weight: bold;
  letter-spacing: 1px;
  text-shadow: 0 2px 4px rgba(0,0,0,0.05);
}

.subtitle {
  color: #a09585;
  font-size: 18px;
  margin-top: 5px;
  text-align: center;
  font-weight: 500;
}

.header-decoration {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  pointer-events: none;
}

.flower-pattern {
  position: absolute;
  top: 20px;
  left: 30px;
  width: 60px;
  height: 60px;
  background: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 100 100'%3E%3Cpath d='M50,15 C65,15 75,25 75,40 C75,55 65,65 50,65 C35,65 25,55 25,40 C25,25 35,15 50,15 Z' fill='%23d4c19c' opacity='0.6'/%3E%3C/svg%3E") no-repeat;
  background-size: contain;
}

.thread-pattern {
  position: absolute;
  bottom: 20px;
  right: 30px;
  width: 100px;
  height: 40px;
  background: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 100 40'%3E%3Cpath d='M10,20 Q30,5 50,20 T90,20' stroke='%23d4c19c' stroke-width='2' fill='none' opacity='0.6'/%3E%3C/svg%3E") no-repeat;
  background-size: contain;
}

.dashboard-main {
  padding: 30px 30px 60px;
  display: flex;
  flex-direction: column;
}

.content-with-sidebar {
  display: flex;
  gap: 30px;
  width: 100%;
  min-height: calc(100vh - 200px); /* 确保内容区最小高度，与左侧对齐 */
}

.main-content {
  flex: 1;
  min-width: 0;
}

.announcement-sidebar {
  width: 320px;
  position: sticky;
  top: 0px;
  height: fit-content;
}

.announcement-card {
  background: white;
  border-radius: 20px;
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.08);
  border: 1px solid #e9dcc8;
  overflow: hidden;
  height: fit-content;
}

.announcement-header {
  background: linear-gradient(135deg, #f9f2eb 0%, #fdfaf3 100%);
  padding: 20px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-bottom: 1px solid #e9dcc8;
}

.announcement-header h3 {
  color: #8B7D6B;
  margin: 0;
  font-size: 18px;
  font-weight: 600;
}

.announcement-count {
  color: #d48341;
  font-size: 14px;
  background: #fff9f2;
  padding: 4px 10px;
  border-radius: 12px;
}

.announcement-list {
  max-height: 500px;
  overflow-y: auto;
  padding: 10px 0;
}

.announcement-item {
  padding: 16px 20px;
  border-bottom: 1px solid #f5f0e6;
  cursor: pointer;
  transition: all 0.3s ease;
  position: relative;
}

.announcement-item:last-child {
  border-bottom: none;
}

.announcement-item:hover {
  background: #fffdf9;
  transform: translateX(5px);
}

.announcement-badge {
  position: absolute;
  top: 12px;
  right: 20px;
  background: #ff6b6b;
  color: white;
  font-size: 10px;
  padding: 2px 6px;
  border-radius: 10px;
  z-index: 2;
}

.announcement-title {
  color: #8B7D6B;
  font-size: 16px;
  font-weight: 600;
  margin: 0 0 8px 0;
  line-height: 1.4;
}

.announcement-preview {
  color: #a09585;
  font-size: 13px;
  margin: 0 0 8px 0;
  line-height: 1.4;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.announcement-time {
  color: #b8aead;
  font-size: 12px;
  margin: 0;
}

.no-announcements {
  text-align: center;
  padding: 40px 20px;
  color: #a09585;
}

.announcement-footer {
  padding: 15px 20px;
  text-align: center;
  border-top: 1px solid #e9dcc8;
  background: #fffdf9;
}

.welcome-section {
  text-align: center;
  margin-bottom: 40px;
  padding: 25px;
  background: linear-gradient(135deg, #fffdf9 0%, #fefefe 100%);
  border-radius: 20px;
  box-shadow: 0 6px 16px rgba(0, 0, 0, 0.05);
  border: 1px solid #e9dcc8;
  position: relative;
  overflow: hidden;
}

.welcome-section::before {
  content: "";
  position: absolute;
  top: -50%;
  left: -50%;
  width: 200%;
  height: 200%;
  background: conic-gradient(
    transparent,
    rgba(139, 125, 107, 0.1),
    transparent 30%
  );
  animation: rotate 6s linear infinite;
}

.welcome-section > * {
  position: relative;
  z-index: 1;
}

.welcome-section h2 {
  color: #8B7D6B;
  font-size: 26px;
  margin-bottom: 10px;
  font-weight: 600;
}

.welcome-section p {
  color: #a09585;
  font-size: 16px;
  font-weight: 500;
}

@keyframes rotate {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

.carousel-wrapper {
  margin-bottom: 50px;
  border-radius: 20px;
  overflow: hidden;
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.12);
  position: relative;
}

.carousel-item {
  cursor: pointer;
  border-radius: 20px;
  overflow: hidden;
}

.carousel-card {
  height: 100%;
  border-radius: 20px;
  overflow: hidden;
  position: relative;
  background: #fff;
  transition: all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275);
}

.carousel-card:hover {
  transform: translateY(-10px) scale(1.01);
  box-shadow: 0 20px 50px rgba(212, 131, 65, 0.25);
}

.carousel-image-wrapper {
  width: 100%;
  height: 100%;
  position: relative;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
}

.carousel-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.5s ease;
}

.carousel-card:hover .carousel-image {
  transform: scale(1.08);
}

.default-image {
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #f9f2eb 0%, #f5f0e6 100%);
  color: #d48341;
  font-size: 60px;
  text-align: center;
}

.default-image p {
  margin-top: 10px;
  color: #a09585;
  font-size: 16px;
}

.crochet-placeholder-icon {
  font-style: normal;
  font-weight: bold;
  font-size: 48px;
}

.carousel-content-overlay {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  background: linear-gradient(transparent, rgba(0, 0, 0, 0.8));
  color: white;
  padding: 80px 30px 30px;
  border-radius: 0 0 20px 20px;
}

.carousel-content {
  max-width: 90%;
}

.carousel-title {
  font-size: 24px;
  margin: 0 0 12px;
  font-weight: bold;
  text-shadow: 1px 1px 4px rgba(0, 0, 0, 0.9);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.carousel-description {
  font-size: 15px;
  margin: 0 0 20px;
  text-shadow: 1px 1px 3px rgba(0, 0, 0, 0.9);
  line-height: 1.5;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.carousel-meta {
  display: flex;
  gap: 25px;
}

.meta-item {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  background: rgba(255, 255, 255, 0.2);
  padding: 6px 14px;
  border-radius: 20px;
  backdrop-filter: blur(4px);
  transition: all 0.3s ease;
}

.meta-item:hover {
  background: rgba(255, 255, 255, 0.3);
  transform: scale(1.05);
}

.recent-posts-section h3 {
  font-size: 24px;
  color: #8B7D6B;
  margin-bottom: 25px;
  text-align: center;
  font-weight: 600;
}

.posts-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 25px;
}

.post-card {
  background: white;
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 6px 16px rgba(0, 0, 0, 0.08);
  transition: all 0.3s ease;
  cursor: pointer;
  border: 1px solid #f0e9dd;
}

.post-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 12px 24px rgba(212, 131, 65, 0.15);
}

.post-image-wrapper {
  width: 100%;
  height: 180px;
  position: relative;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
}

.post-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.5s ease;
}

.post-card:hover .post-image {
  transform: scale(1.05);
}

.post-default-image {
  width: 100%;
  height: 180px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #f9f2eb 0%, #f5f0e6 100%);
  color: #d48341;
}

.post-content {
  padding: 20px;
}

.post-title {
  font-size: 18px;
  font-weight: 600;
  margin: 0 0 10px;
  color: #8B7D6B;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.post-excerpt {
  font-size: 14px;
  color: #a09585;
  margin: 0 0 15px;
  line-height: 1.5;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.post-stats {
  display: flex;
  gap: 15px;
}

.stat {
  display: flex;
  align-items: center;
  gap: 5px;
  font-size: 13px;
  color: #d48341;
  background: #f9f2eb;
  padding: 5px 12px;
  border-radius: 20px;
}

/* Element Plus Carousel 指示器样式 */
:deep(.el-carousel__indicator button) {
  background-color: #d48341;
  opacity: 0.6;
  width: 12px;
  height: 4px;
  border-radius: 2px;
}

:deep(.el-carousel__indicator.is-active button) {
  opacity: 1;
  width: 20px;
}

:deep(.el-carousel__arrow) {
  background-color: rgba(255, 255, 255, 0.7) !important;
  color: #8B7D6B;
  border-radius: 50%;
  width: 44px;
  height: 44px;
  backdrop-filter: blur(4px);
  border: 1px solid rgba(255, 255, 255, 0.5);
  transition: all 0.3s ease;
}

:deep(.el-carousel__arrow:hover) {
  background-color: rgba(255, 255, 255, 0.9) !important;
  transform: scale(1.1);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

@media (max-width: 1100px) {
  .content-with-sidebar {
    flex-direction: column;
  }
  
  .announcement-sidebar {
    width: 100%;
    position: static;
  }
}

@media (max-width: 768px) {
  .dashboard-header {
    padding: 30px 20px;
  }
  
  .dashboard-header h1 {
    font-size: 28px;
  }
  
  .subtitle {
    font-size: 16px;
  }
  
  .dashboard-main {
    padding: 20px 15px 40px;
  }
  
  .carousel-title {
    font-size: 20px;
  }
  
  .carousel-description {
    font-size: 14px;
  }
}
</style>